import { useCallback, useEffect, useState, useContext, useMemo } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import useUser, { deleteAuthorized, getAuthorized, isUserDoctor, postAuthorized, patchAuthorized } from "../../../hooks/User";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { getPatientDisplayName, getPatientInitials } from "../../../utils/patientUtils";
import { ROUTES } from "../../../routes";
import { MiniTutorial, PATIENT_MEDICATIONS_STEPS } from "../../../components/MiniTutorial";
import {
    PlusIcon,
    ClockIcon,
    CalendarIcon,
    RepeatIcon,
    BellIcon,
    MapPinIcon,
    EditIcon,
    TrashIcon,
    XIcon,
    ChevronLeftIcon,
    AlertIcon,
    PillIcon,
    CheckIcon,
    StethoscopeIcon,
    FileTextIcon,
    ChevronRightIcon,
    SearchIcon
} from "../../../components/icons";
import SEO from "../../../components/SEO";

import "./patientmedications.css";

// Constants
const FrequencyTypeLabels = {
    once_per_day: "1 раз в день",
    multiple_times_per_day: "Несколько раз в день",
    every_few_days: "Раз в несколько дней",
    weekly: "По дням недели"
};

const WeekDays = [
    { value: 0, label: "Пн", full: "Понедельник" },
    { value: 1, label: "Вт", full: "Вторник" },
    { value: 2, label: "Ср", full: "Среда" },
    { value: 3, label: "Чт", full: "Четверг" },
    { value: 4, label: "Пт", full: "Пятница" },
    { value: 5, label: "Сб", full: "Суббота" },
    { value: 6, label: "Вс", full: "Воскресенье" }
];

const TimezoneOptions = [
    { value: "Europe/Moscow", label: "Москва (UTC+3)" },
    { value: "Europe/Samara", label: "Самара (UTC+4)" },
    { value: "Asia/Yekaterinburg", label: "Екатеринбург (UTC+5)" },
    { value: "Asia/Novosibirsk", label: "Новосибирск (UTC+7)" },
    { value: "Asia/Vladivostok", label: "Владивосток (UTC+10)" }
];

// Helper: Format time from HH:MM:SS to HH:MM
function formatTime(timeString) {
    if (!timeString) return "";
    return timeString.substring(0, 5);
}

// Helper: Get frequency label
function getFrequencyLabel(schedule) {
    const baseLabel = FrequencyTypeLabels[schedule.frequency_type] || schedule.frequency_type;
    
    if (schedule.frequency_type === "weekly" && schedule.days_of_week) {
        const days = schedule.days_of_week
            .sort((a, b) => a - b)
            .map(d => WeekDays.find(w => w.value === d)?.label)
            .join(", ");
        return baseLabel + " (" + days + ")";
    }
    
    if (schedule.frequency_type === "every_few_days" && schedule.interval_days) {
        return baseLabel + " (каждые " + schedule.interval_days + " дн.)";
    }
    
    if (schedule.frequency_type === "multiple_times_per_day" && schedule.scheduled_times) {
        const count = schedule.scheduled_times.length;
        return baseLabel + " (" + count + " " + EvaluateCount(count) + ")";
    }
    
    return baseLabel;
}

// Helper functions for word forms
function EvaluateEvery(days) {
    if (days % 10 === 1 && days > 20) return "каждый";
    return "каждые";
}

function EvaluateDays(days) {
    if (days < 5) return "дня";
    if (days <= 20) return "дней";
    if (days % 10 === 1) return "день";
    if (days % 10 < 5 && days % 10 !== 0) return "дня";
    return "дней";
}

function EvaluateCount(count) {
    if (count === 1) return "раз";
    if (count >= 5 && count <= 21) return "раз";
    if (count % 10 < 2 || count % 10 > 4) return "раз";
    return "раза";
}

function EvaluateEveryDelay(delay) {
    if (delay === 1 || (delay > 20 && delay % 10 === 1)) return "каждую";
    return "каждые";
}

function EvaluateDelayMinute(delay) {
    if (delay === 1 || (delay > 20 && delay % 10 === 1)) return "минуту";
    if (delay >= 5 && delay <= 20) return "минут";
    if (delay % 10 === 0 || delay % 10 > 4) return "минут";
    return "минуты";
}

// Patient Header Component
function PatientHeader({ patientId, patientName, onBack }) {
    const patient = patientName ? { full_name: patientName } : null;
    const displayName = getPatientDisplayName(patient || { id: patientId });
    const initials = getPatientInitials(patient || { id: patientId });
    
    return (
        <div className="medications-header">
            <button className="btn-back" onClick={onBack}>
                <ChevronLeftIcon />
                <span>Назад к пациентам</span>
            </button>
            <div className="patient-header-info">
                <div className="patient-avatar-large">{initials}</div>
                <div className="patient-header-details">
                    <h1>Назначения лекарств</h1>
                    <p className="patient-name">{displayName}</p>
                </div>
            </div>
        </div>
    );
}

// Medication Card Component
function MedicationCard({ schedule, onEdit, onDelete }) {
    const isActive = schedule.is_active !== false;
    const times = schedule.scheduled_times?.map(formatTime).join(", ") || "—";
    
    return (
        <div className={`medication-card ${!isActive ? 'inactive' : ''}`}>
            <div className="medication-card-header">
                <div className="medication-icon">
                    <PillIcon />
                </div>
                <div className="medication-status">
                    <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                        {isActive ? "Активно" : "Неактивно"}
                    </span>
                </div>
            </div>
            
            <div className="medication-content">
                <h3 className="medication-name" title={schedule.medication_name}>
                    {schedule.medication_name}
                </h3>
                
                {schedule.dosage && (
                    <p className="medication-dosage">{schedule.dosage}</p>
                )}
                
                {schedule.instructions && (
                    <p className="medication-instructions" title={schedule.instructions}>
                        {schedule.instructions}
                    </p>
                )}
            </div>
            
            <div className="medication-schedule">
                <div className="schedule-row">
                    <RepeatIcon />
                    <span>{getFrequencyLabel(schedule)}</span>
                </div>
                <div className="schedule-row">
                    <ClockIcon />
                    <span>{times}</span>
                </div>
                <div className="schedule-row">
                    <CalendarIcon />
                    <span>
                        {dayjs(schedule.start_date).format("DD.MM.YYYY")}
                        {schedule.end_date && (" — " + dayjs(schedule.end_date).format("DD.MM.YYYY"))}
                    </span>
                </div>
            </div>
            
            <div className="medication-actions">
                <button 
                    className="btn-action btn-edit"
                    onClick={() => onEdit(schedule)}
                    title="Редактировать"
                >
                    <EditIcon />
                    <span>Изменить</span>
                </button>
                <button 
                    className="btn-action btn-delete"
                    onClick={() => onDelete(schedule)}
                    title="Удалить"
                >
                    <TrashIcon />
                    <span>Удалить</span>
                </button>
            </div>
        </div>
    );
}

// Medication Form Drawer Component
function MedicationDrawer({ isOpen, onClose, onSave, initialData, patientId }) {
    const [formData, setFormData] = useState({
        medication_name: "",
        dosage: "",
        instructions: "",
        frequency_type: "once_per_day",
        scheduled_times: ["09:00"],
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: "",
        timezone: "Europe/Moscow",
        reminder_minutes_before: 10,
        max_snooze_count: 3,
        snooze_duration_minutes: 10,
        interval_days: 2,
        days_of_week: [],
        is_active: true
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeSection, setActiveSection] = useState("basic");
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const isEditing = !!initialData?.id;

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    medication_name: initialData.medication_name || "",
                    dosage: initialData.dosage || "",
                    instructions: initialData.instructions || "",
                    frequency_type: initialData.frequency_type || "once_per_day",
                    scheduled_times: initialData.scheduled_times?.map(formatTime) || ["09:00"],
                    start_date: initialData.start_date || dayjs().format("YYYY-MM-DD"),
                    end_date: initialData.end_date || "",
                    timezone: initialData.timezone || "Europe/Moscow",
                    reminder_minutes_before: initialData.reminder_minutes_before ?? 10,
                    snooze_duration_minutes: initialData.snooze_duration_minutes ?? 10,
                    max_snooze_count: initialData.max_snooze_count ?? 3,
                    interval_days: initialData.interval_days || 2,
                    days_of_week: initialData.days_of_week || [],
                    is_active: initialData.is_active !== false
                });
            } else {
                setFormData({
                    medication_name: "",
                    dosage: "",
                    instructions: "",
                    frequency_type: "once_per_day",
                    scheduled_times: ["09:00"],
                    start_date: dayjs().format("YYYY-MM-DD"),
                    end_date: "",
                    timezone: "Europe/Moscow",
                    reminder_minutes_before: 10,
                    snooze_duration_minutes: 10,
                    max_snooze_count: 3,
                    interval_days: 2,
                    days_of_week: [],
                    is_active: true
                });
            }
            setErrors({});
            setActiveSection("basic");
        }
    }, [initialData, isOpen]);

    // Close on escape key (ignore when picker is open)
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen && !isPickerOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, isPickerOpen]);

    // Prevent body scroll when drawer is open (use counter to cooperate with nested overlays)
    useEffect(() => {
        if (isOpen) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = prevOverflow;
            };
        }
    }, [isOpen]);

    const validate = () => {
        const newErrors = {};
        
        if (!formData.medication_name.trim()) {
            newErrors.medication_name = "Введите название лекарства";
            setActiveSection("basic");
        }
        if (!formData.dosage.trim()) {
            newErrors.dosage = "Введите дозировку";
            setActiveSection("basic");
        }
        if (formData.frequency_type === "weekly" && !formData.days_of_week.length) {
            newErrors.days_of_week = "Выберите хотя бы один день недели";
            setActiveSection("schedule");
        }
        if (!formData.start_date) {
            newErrors.start_date = "Укажите дату начала";
            setActiveSection("period");
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        try {
            const payload = {
                patient_id: patientId,
                ...formData
            };
            
            if (!payload.end_date) {
                delete payload.end_date;
            }
            
            if (payload.frequency_type !== "every_few_days") {
                delete payload.interval_days;
            }
            if (payload.frequency_type !== "weekly") {
                delete payload.days_of_week;
            }
            // Calculate times_per_day from scheduled_times length
            payload.times_per_day = payload.scheduled_times.length;
            
            await onSave(payload, isEditing ? initialData.id : null);
            onClose();
        } catch (error) {
            console.error("Failed to save medication:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const generateAutoTimes = (count) => {
        if (count < 1 || count > 24) return ["09:00"];
        const times = [];
        const startHour = 9;
        const endHour = 21;
        const interval = (endHour - startHour) / (count - 1 || 1);
        
        for (let i = 0; i < count; i++) {
            const hour = Math.round(startHour + interval * i);
            const time = hour.toString().padStart(2, '0') + ':00';
            times.push(time);
        }
        return times;
    };

    const toggleDayOfWeek = (day) => {
        setFormData(prev => ({
            ...prev,
            days_of_week: prev.days_of_week.includes(day)
                ? prev.days_of_week.filter(d => d !== day)
                : [...prev.days_of_week, day].sort()
        }));
    };

    const addTime = () => {
        if (formData.scheduled_times.length >= 24) return;
        setFormData(prev => {
            const newTimes = [...prev.scheduled_times, "12:00"];
            // Auto-switch to multiple times per day if adding 2nd+ time
            if (prev.frequency_type === "once_per_day" && newTimes.length >= 2) {
                return {
                    ...prev,
                    frequency_type: "multiple_times_per_day",
                    scheduled_times: newTimes
                };
            }
            return {
                ...prev,
                scheduled_times: newTimes
            };
        });
    };

    const updateTime = (index, value) => {
        setFormData(prev => ({
            ...prev,
            scheduled_times: prev.scheduled_times.map((t, i) => i === index ? value : t)
        }));
    };

    const removeTime = (index) => {
        if (formData.scheduled_times.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            scheduled_times: prev.scheduled_times.filter((_, i) => i !== index)
        }));
    };

    const handleFrequencyChange = (newType) => {
        setFormData(prev => ({
            ...prev,
            frequency_type: newType,
            scheduled_times: ["09:00"]
        }));
    };

    const clearError = (field) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="drawer-backdrop" onClick={onClose} />
            
            {/* Drawer */}
            <div className="drawer">
                <div className="drawer-header">
                    <div className="drawer-header-content">
                        <h2>{isEditing ? "Редактировать назначение" : "Новое назначение"}</h2>
                        <button className="drawer-close" onClick={onClose}>
                            <XIcon />
                        </button>
                    </div>
                </div>
                
                <form onSubmit={handleSubmit} className="drawer-form">
                    <div className="drawer-body">
                        {/* Section Navigation */}
                        <div className="section-nav">
                            <button 
                                type="button"
                                className={`section-nav-item ${activeSection === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveSection('basic')}
                            >
                                <StethoscopeIcon size={18} />
                                <span>Основное</span>
                                {(errors.medication_name || errors.dosage) && <span className="nav-error">!</span>}
                            </button>
                            <button 
                                type="button"
                                className={`section-nav-item ${activeSection === 'schedule' ? 'active' : ''}`}
                                onClick={() => setActiveSection('schedule')}
                            >
                                <RepeatIcon size={18} />
                                <span>Расписание</span>
                                {errors.days_of_week && <span className="nav-error">!</span>}
                            </button>
                            <button 
                                type="button"
                                className={`section-nav-item ${activeSection === 'period' ? 'active' : ''}`}
                                onClick={() => setActiveSection('period')}
                            >
                                <CalendarIcon size={18} />
                                <span>Период</span>
                                {errors.start_date && <span className="nav-error">!</span>}
                            </button>
                            <button 
                                type="button"
                                className={`section-nav-item ${activeSection === 'notifications' ? 'active' : ''}`}
                                onClick={() => setActiveSection('notifications')}
                            >
                                <BellIcon size={18} />
                                <span>Уведомления</span>
                            </button>
                        </div>

                        {/* Basic Info Section */}
                        {activeSection === "basic" && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <StethoscopeIcon size={20} />
                                    <h3>Основная информация</h3>
                                </div>
                                
                                <div className="form-field">
                                    <label>Название лекарства *</label>
                                    <div className="medication-name-input-wrap">
                                        <input
                                            type="text"
                                            value={formData.medication_name}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, medication_name: e.target.value }));
                                                clearError('medication_name');
                                            }}
                                            placeholder="Например: Аспирин"
                                            className={errors.medication_name ? "error" : ""}
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="btn-open-medication-picker"
                                            onClick={() => setIsPickerOpen(true)}
                                            title="Найти в справочнике"
                                        >
                                            <SearchIcon size={18} />
                                        </button>
                                    </div>
                                    <span className="field-hint">Можно ввести вручную или выбрать из справочника</span>
                                    {errors.medication_name && <span className="field-error">{errors.medication_name}</span>}
                                </div>
                                
                                <div className="form-field">
                                    <label>Дозировка *</label>
                                    <input
                                        type="text"
                                        value={formData.dosage}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, dosage: e.target.value }));
                                            clearError('dosage');
                                        }}
                                        placeholder="Например: 1 таблетка (100мг)"
                                        className={errors.dosage ? "error" : ""}
                                    />
                                    {errors.dosage && <span className="field-error">{errors.dosage}</span>}
                                </div>
                                
                                <div className="form-field">
                                    <label>Инструкции</label>
                                    <textarea
                                        value={formData.instructions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                                        placeholder="Например: Принимать после еды, запивая водой"
                                        rows={4}
                                    />
                                </div>

                                {isEditing && (
                                    <label className="checkbox-field">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                        />
                                        <span>Назначение активно</span>
                                    </label>
                                )}
                            </div>
                        )}

                        {/* Schedule Section */}
                        {activeSection === "schedule" && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <RepeatIcon size={20} />
                                    <h3>Расписание приёма</h3>
                                </div>
                                
                                <div className="form-field">
                                    <label>Частота приёма</label>
                                    <div className="frequency-options">
                                        {[
                                            { value: "once_per_day", label: "1 раз в день", icon: "1x" },
                                            { value: "multiple_times_per_day", label: "Несколько раз", icon: "Nx" },
                                            { value: "every_few_days", label: "Раз в несколько дней", icon: "↻" },
                                            { value: "weekly", label: "По дням недели", icon: "📅" }
                                        ].map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`frequency-option ${formData.frequency_type === option.value ? 'active' : ''}`}
                                                onClick={() => handleFrequencyChange(option.value)}
                                            >
                                                <span className="frequency-icon">{option.icon}</span>
                                                <span className="frequency-label">{option.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Dynamic fields based on frequency */}
                                {formData.frequency_type === "weekly" && (
                                    <div className="form-field">
                                        <label>Дни недели *</label>
                                        <div className={`weekdays-grid ${errors.days_of_week ? 'error' : ''}`}>
                                            {WeekDays.map((day) => (
                                                <button
                                                    key={day.value}
                                                    type="button"
                                                    className={`weekday-chip ${formData.days_of_week.includes(day.value) ? 'active' : ''}`}
                                                    onClick={() => {
                                                        toggleDayOfWeek(day.value);
                                                        clearError('days_of_week');
                                                    }}
                                                    title={day.full}
                                                >
                                                    {day.label}
                                                </button>
                                            ))}
                                        </div>
                                        {errors.days_of_week && <span className="field-error">{errors.days_of_week}</span>}
                                    </div>
                                )}

                                {formData.frequency_type === "every_few_days" && (
                                    <div className="form-field">
                                        <label>Интервал</label>
                                        <div className="interval-control">
                                            <span>{EvaluateEvery(formData.interval_days)}</span>
                                            <input
                                                type="number"
                                                min="2"
                                                max="30"
                                                value={formData.interval_days}
                                                onChange={(e) => setFormData(prev => ({ 
                                                    ...prev, 
                                                    interval_days: Math.max(2, parseInt(e.target.value) || 2)
                                                }))}
                                            />
                                            <span>{EvaluateDays(formData.interval_days)}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Time inputs */}
                                <div className="form-field">
                                    <label>Время приёма</label>
                                    <div className="time-chips">
                                        {formData.scheduled_times.map((time, index) => (
                                            <div key={index} className="time-chip">
                                                <input
                                                    type="time"
                                                    value={time}
                                                    onChange={(e) => updateTime(index, e.target.value)}
                                                />
                                                {formData.scheduled_times.length > 1 && (
                                                    <button 
                                                        type="button" 
                                                        className="time-chip-remove"
                                                        onClick={() => removeTime(index)}
                                                    >
                                                        <XIcon size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button type="button" className="time-chip-add" onClick={addTime}>
                                            <PlusIcon size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Period Section */}
                        {activeSection === "period" && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <CalendarIcon size={20} />
                                    <h3>Период действия</h3>
                                </div>
                                
                                <div className="form-field">
                                    <label>Дата начала *</label>
                                    <input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, start_date: e.target.value }));
                                            clearError('start_date');
                                        }}
                                        min={dayjs().format("YYYY-MM-DD")}
                                        className={errors.start_date ? "error" : ""}
                                    />
                                    {errors.start_date && <span className="field-error">{errors.start_date}</span>}
                                </div>
                                
                                <div className="form-field">
                                    <label>Дата окончания <span className="optional">(необязательно)</span></label>
                                    <input
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                        min={formData.start_date}
                                    />
                                    {formData.end_date && (
                                        <button 
                                            type="button" 
                                            className="clear-date"
                                            onClick={() => setFormData(prev => ({ ...prev, end_date: "" }))}
                                        >
                                            Очистить дату
                                        </button>
                                    )}
                                </div>

                                <div className="form-field">
                                    <label>Часовой пояс</label>
                                    <select
                                        value={formData.timezone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                                    >
                                        {TimezoneOptions.map(tz => (
                                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Notifications Section */}
                        {activeSection === "notifications" && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <BellIcon size={20} />
                                    <h3>Настройки уведомлений</h3>
                                </div>
                                
                                <div className="form-field">
                                    <label>Напомнить за</label>
                                    <div className="reminder-control">
                                        <input
                                            type="number"
                                            min="0"
                                            max="60"
                                            value={formData.reminder_minutes_before}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                reminder_minutes_before: Math.max(0, parseInt(e.target.value) || 0)
                                            }))}
                                        />
                                        <span>минут до приёма</span>
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label>Повторные напоминания</label>
                                    <div className="reminder-control">
                                        <input
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={formData.max_snooze_count}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                max_snooze_count: Math.max(0, parseInt(e.target.value) || 0)
                                            }))}
                                        />
                                        <span>{EvaluateCount(formData.max_snooze_count)}</span>
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label>Интервал повторов</label>
                                    <div className="reminder-control">
                                        <input
                                            type="number"
                                            min="5"
                                            max="60"
                                            value={formData.snooze_duration_minutes}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                snooze_duration_minutes: Math.max(5, parseInt(e.target.value) || 5)
                                            }))}
                                        />
                                        <span>{EvaluateDelayMinute(formData.snooze_duration_minutes)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="drawer-footer">
                        <div className="drawer-footer-nav">
                            {activeSection !== "basic" && (
                                <button 
                                    type="button" 
                                    className="btn-nav-prev"
                                    onClick={() => {
                                        const sections = ["basic", "schedule", "period", "notifications"];
                                        const idx = sections.indexOf(activeSection);
                                        if (idx > 0) setActiveSection(sections[idx - 1]);
                                    }}
                                >
                                    <ChevronLeftIcon size={16} />
                                    Назад
                                </button>
                            )}
                            {activeSection !== "notifications" ? (
                                <button 
                                    type="button" 
                                    className="btn-nav-next"
                                    onClick={() => {
                                        const sections = ["basic", "schedule", "period", "notifications"];
                                        const idx = sections.indexOf(activeSection);
                                        if (idx < sections.length - 1) setActiveSection(sections[idx + 1]);
                                    }}
                                >
                                    Далее
                                    <ChevronRightIcon size={16} />
                                </button>
                            ) : null}
                        </div>
                        <div className="drawer-footer-actions">
                            <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>
                                Отмена
                            </button>
                            <button type="submit" className="btn-primary" disabled={isLoading}>
                                {isLoading ? (
                                    "Сохранение..."
                                ) : (
                                    <>
                                        <CheckIcon size={18} />
                                        {isEditing ? "Сохранить" : "Создать"}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <MedicationSelectionModal
                isOpen={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                initialQuery={formData.medication_name}
                onSelect={(med) => {
                    setFormData(prev => ({ ...prev, medication_name: med.name }));
                    clearError('medication_name');
                }}
            />
        </>
    );
}

// Medication Selection Modal
function MedicationSelectionModal({ isOpen, onClose, onSelect, initialQuery = "" }) {
    const [medications, setMedications] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const { setErrorList } = useContext(ErrorHandlerContext);
    const limit = 10;

    // Reset state when opening and prefill search
    useEffect(() => {
        if (isOpen) {
            const initial = (initialQuery || "").trim();
            setSearchQuery(initial);
            setDebouncedQuery(initial);
            setCurrentPage(1);
            setTotalCount(0);
            setMedications([]);
        }
    }, [isOpen, initialQuery]);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch medications
    useEffect(() => {
        if (!isOpen) return;

        const abortController = new AbortController();

        const fetchMedications = async () => {
            setLoading(true);
            try {
                const params = {
                    st: (currentPage - 1) * limit,
                    limit
                };
                if (debouncedQuery.trim()) {
                    params.query = debouncedQuery.trim();
                }
                const response = await getAuthorized("/doctor/get_medication_names", {
                    params,
                    signal: abortController.signal
                });
                if (abortController.signal.aborted) return;
                setMedications(response.data?.medications || []);
                setTotalCount(response.data?.total_count || 0);
            } catch (err) {
                if (abortController.signal.aborted) return;
                console.error("Failed to load medication names:", err);
                setErrorList(prev => [...prev, err.response?.data?.error_description || "Ошибка при загрузке справочника лекарств"]);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchMedications();

        return () => abortController.abort();
    }, [isOpen, debouncedQuery, currentPage, setErrorList]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open (preserve parent overlay lock)
    useEffect(() => {
        if (isOpen) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = prevOverflow;
            };
        }
    }, [isOpen]);

    const totalPages = Math.max(1, Math.ceil(totalCount / limit));
    const hasNext = currentPage < totalPages;
    const hasPrev = currentPage > 1;

    const handleSelect = (medication) => {
        onSelect(medication);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay medication-picker-overlay" onClick={onClose}>
            <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Выберите лекарство</h2>
                    <button className="modal-close" onClick={onClose}>
                        <XIcon />
                    </button>
                </div>
                <div className="modal-body">
                    <div className="search-bar">
                        <SearchIcon />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по названию..."
                            autoFocus
                        />
                    </div>
                    {loading ? (
                        <div className="picker-loading-state">
                            <div className="spinner"></div>
                            <span>Загрузка...</span>
                        </div>
                    ) : medications.length === 0 ? (
                        <div className="picker-empty-state">
                            {debouncedQuery.trim()
                                ? "Лекарства не найдены"
                                : "Начните вводить название для поиска"}
                        </div>
                    ) : (
                        <>
                            <div className="selection-list">
                                {medications.map((med) => (
                                    <div
                                        key={med.id}
                                        className="selection-item"
                                        onClick={() => handleSelect(med)}
                                    >
                                        <div className="selection-icon">
                                            <PillIcon />
                                        </div>
                                        <div className="selection-info">
                                            <span className="selection-name">{med.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {totalCount > limit && (
                                <div className="modal-pagination">
                                    <button
                                        type="button"
                                        className="btn-page"
                                        onClick={() => setCurrentPage(p => p - 1)}
                                        disabled={!hasPrev || loading}
                                    >
                                        <ChevronLeftIcon size={16} /> Назад
                                    </button>
                                    <span className="page-info">
                                        Страница {currentPage} из {totalPages} ({totalCount} всего)
                                    </span>
                                    <button
                                        type="button"
                                        className="btn-page"
                                        onClick={() => setCurrentPage(p => p + 1)}
                                        disabled={!hasNext || loading}
                                    >
                                        Вперёд <ChevronRightIcon size={16} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// Delete Confirmation Modal
function DeleteConfirmModal({ medication, onConfirm, onCancel }) {
    if (!medication) return null;
    
    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <AlertIcon size={48} />
                </div>
                <h3>Удалить назначение?</h3>
                <p>Вы уверены, что хотите удалить назначение <strong>"{medication.medication_name}"</strong>?</p>
                <p className="confirm-hint">Это действие нельзя отменить.</p>
                
                <div className="confirm-actions">
                    <button className="btn-secondary" onClick={onCancel}>Отмена</button>
                    <button className="btn-danger" onClick={onConfirm}>Удалить</button>
                </div>
            </div>
        </div>
    );
}

// Main Component
function PatientMedications() {
    const { data, error, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const { setErrorList } = useContext(ErrorHandlerContext);
    
    const [patientId, setPatientId] = useState(null);
    const [patientName, setPatientName] = useState("");
    const [medications, setMedications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingMedication, setEditingMedication] = useState(null);
    const [medicationToDelete, setMedicationToDelete] = useState(null);
    
    // Auth check
    useEffect(() => {
        if (userLoading) return;
        if (error) {
            navigate(ROUTES.AUTH, { viewTransition: true });
            return;
        }
        if (!isUserDoctor(data)) {
            navigate(ROUTES.PROFILE, { viewTransition: true });
            return;
        }
        
        const id = location?.state?.patientId;
        if (id === null || id === undefined) {
            navigate(ROUTES.PATIENTS, { viewTransition: true });
            return;
        }
        
        setPatientId(id);
        setPatientName(location?.state?.patientName || "");
    }, [data, error, userLoading, navigate, location]);
    
    // Load patient name if not provided
    useEffect(() => {
        if (!patientId || patientName) return;
        
        getAuthorized("/doctor/my_patients", {})
            .then(response => {
                const patients = response.data?.patients || [];
                const patient = patients.find(p => p.id === patientId);
                if (patient) {
                    setPatientName(getPatientDisplayName(patient));
                }
            })
            .catch(err => console.error("Failed to load patient info:", err));
    }, [patientId, patientName]);
    
    // Load medications
    const loadMedications = useCallback(async () => {
        if (!patientId) return;
        
        setLoading(true);
        try {
            const response = await getAuthorized("/doctor/get_patient_medications", {
                params: { patient_id: patientId, include_inactive: true }
            });
            setMedications(response.data || []);
        } catch (err) {
            console.error("Failed to load medications:", err);
            setErrorList(prev => [...prev, err.response?.data?.error_description || "Ошибка при загрузке назначений"]);
        } finally {
            setLoading(false);
        }
    }, [patientId, setErrorList]);
    
    useEffect(() => {
        loadMedications();
    }, [loadMedications]);
    
    // Save handlers
    const handleSave = async (payload, medicationId) => {
        try {
            if (medicationId) {
                await patchAuthorized("/doctor/update_medication_schedule/" + medicationId, payload);
            } else {
                await postAuthorized("/doctor/create_medication_schedule", payload);
            }
            loadMedications();
        } catch (err) {
            console.error("Failed to save medication:", err);
            setErrorList(prev => [...prev, err.response?.data?.error_description || "Ошибка при сохранении назначения"]);
            throw err;
        }
    };
    
    // Edit handlers
    const handleEdit = useCallback((medication) => {
        setEditingMedication(medication);
        setIsDrawerOpen(true);
    }, []);
    
    const handleCreate = useCallback(() => {
        setEditingMedication(null);
        setIsDrawerOpen(true);
    }, []);
    
    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setEditingMedication(null);
    }, []);
    
    // Delete handlers
    const handleDeleteClick = useCallback((medication) => {
        setMedicationToDelete(medication);
    }, []);
    
    const handleDeleteConfirm = useCallback(async () => {
        if (!medicationToDelete) return;
        
        try {
            await deleteAuthorized("/doctor/delete_medication_schedule/" + medicationToDelete.id);
            setMedicationToDelete(null);
            loadMedications();
        } catch (err) {
            console.error("Failed to delete medication:", err);
            setErrorList(prev => [...prev, err.response?.data?.error_description || "Ошибка при удалении назначения"]);
        }
    }, [medicationToDelete, loadMedications, setErrorList]);
    
    const handleDeleteCancel = useCallback(() => {
        setMedicationToDelete(null);
    }, []);
    
    const handleBack = useCallback(() => {
        navigate(ROUTES.PATIENTS, { viewTransition: true });
    }, [navigate]);
    
    // Loading state
    if (userLoading || loading) {
        return (
            <div className="medications-page">
                <ProfileNavigation isDoctor={true} />
                <div className="medications-loading">
                    <div className="spinner"></div>
                    <p>Загрузка назначений...</p>
                </div>
            </div>
        );
    }
    
    const activeCount = medications.filter(m => m.is_active !== false).length;
    
    return (
        <div className="medications-page">
            <ProfileNavigation isDoctor={isUserDoctor(data)} />
            
            <div className="medications-container">
                <PatientHeader 
                    patientId={patientId} 
                    patientName={patientName}
                    onBack={handleBack}
                />
                
                <div className="medications-toolbar">
                    <div className="medications-stats">
                        <span>Всего назначений: {medications.length}</span>
                        <span className="stats-active">Активных: {activeCount}</span>
                    </div>
                    <button className="btn-primary btn-create" onClick={handleCreate}>
                        <PlusIcon />
                        <span>Назначить лекарство</span>
                    </button>
                </div>
                
                {medications.length === 0 ? (
                    <div className="empty-medications-state">
                        <div className="empty-icon">
                            <PillIcon size={40} />
                        </div>
                        <h3>Нет назначенных лекарств</h3>
                        <p>У этого пациента пока нет назначений лекарств</p>
                    </div>
                ) : (
                    <div className="medications-grid">
                        {medications.map((medication) => (
                            <MedicationCard
                                key={medication.id}
                                schedule={medication}
                                onEdit={handleEdit}
                                onDelete={handleDeleteClick}
                            />
                        ))}
                    </div>
                )}
            </div>
            
            {/* Drawer */}
            <MedicationDrawer
                isOpen={isDrawerOpen}
                onClose={handleCloseDrawer}
                onSave={handleSave}
                initialData={editingMedication}
                patientId={patientId}
            />
            
            {/* Delete Confirmation */}
            <DeleteConfirmModal
                medication={medicationToDelete}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
            
            <MiniTutorial steps={PATIENT_MEDICATIONS_STEPS} pageKey="patient-medications" />
        </div>
    );
}

// Export wrapped with ErrorHandler
export default function PatientMedicationsPage() {
    return (
        <>
            <SEO title="Прием лекарств" description="Управление приемом лекарственных препаратов и назначениями" noindex={true} />
            <ErrorHandlerContextProvider>
                <PatientMedications />
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}
